import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { arrancarTema } from './lib/tema'
import './index.css'
import App from './App.jsx'
import ErrorBoundary from './ErrorBoundary.jsx'
import { ProveedorAuth } from './auth.jsx'
import PanelApp from './panel/PanelApp.jsx'
import TiendaApp from './tienda/TiendaApp.jsx'
import TerminalApp from './terminal/TerminalApp.jsx'
import ReportarPorQR from './tickets/ReportarPorQR.jsx'
import { ProveedorAuthPanel } from './panel/auth.jsx'
import { ProveedorAvisos } from './ui/Avisos.jsx'
import PantallaMantenimiento from './consola/PantallaMantenimiento.jsx'
import { anotarWorker } from './integrador/pwa/estadoWorker'
// Captura `beforeinstallprompt` ANTES de que React monte (DIRECTRIZ-PWA F1):
// si lo escuchara un componente, el evento ya habría pasado.
import './integrador/pwa/instalacion'

// Dos aplicaciones, una raíz. La bifurcación es por ruta y ocurre ANTES de
// montar ningún proveedor de sesión: bajo /panel no existe `ProveedorAuth`
// —el de las empresas— y fuera de /panel no existe el del operador del
// servicio. Ninguna pantalla puede leer por descuido la sesión que no le
// toca, porque el contexto sencillamente no está ahí.
//
// nginx ya devuelve index.html para cualquier ruta desconocida, así que
// /panel carga sin configuración adicional.
const enPanel = window.location.pathname.startsWith('/panel');

// LA TIENDA NO TIENE SESIÓN, y por eso se bifurca ANTES de montar ningún
// proveedor: bajo /tienda no existe `ProveedorAuth`, así que ninguna
// pantalla del escaparate puede leer por descuido la sesión de quien haya
// entrado a la consola en ese mismo navegador. Es el mismo criterio que
// separa /panel, y aquí importa más: la tienda se abre desde un QR, a
// veces en la tableta del almacén donde el encargado tiene su sesión viva.
const enTienda = window.location.pathname.startsWith('/tienda');

// LA TERMINAL VIRTUAL TAMPOCO TIENE SESIÓN DE PERSONA, y se bifurca aquí
// por la misma razón que la tienda, solo que aquí importa más.
//
// Esta pantalla es un APARATO: lleva la credencial del dispositivo, con
// su audiencia propia y su vida larga. Si se montara dentro de
// `ProveedorAuth`, una terminal virtual abierta en la misma computadora
// donde el encargado dejó su sesión podría mandar SU token a las rutas
// de aparato — y el kárdex diría que la repisa hizo lo que hizo el
// encargado, que es justo lo que la sesión de operador vino a arreglar.
//
// Bajo /terminal-virtual el contexto sencillamente no está ahí.
const enTerminal = window.location.pathname.startsWith('/terminal-virtual');

// REPORTAR POR QR SIN CUENTA (R-TK4) · DIRECTRIZ-TICKETS F2. Se bifurca
// aquí, ANTES de montar `ProveedorAuth`, por lo mismo que la tienda: quien
// escanea la etiqueta de un activo puede no tener cuenta —un visitante, un
// proveedor—, y solo reporta. Bajo /reportar no existe el contexto de
// sesión, así que ninguna pantalla puede leer por descuido la de nadie.
const enReportarQR = window.location.pathname.startsWith('/reportar');

// ANTES del primer render: aplicarlo dentro de un efecto pinta la
// consola en claro durante una fracción de segundo y luego la apaga, que
// es el destello blanco que molesta justo a quien eligió el modo oscuro.
arrancarTema()

createRoot(document.getElementById('root')).render(
  <StrictMode>
    {/* Envuelve toda la aplicación: una excepción en cualquier vista
        muestra una pantalla de recuperación en lugar de dejar el
        navegador en blanco. */}
    <ErrorBoundary>
      {/* Los avisos envuelven a las dos aplicaciones: un toast no es
          estado de sesión, y el panel también necesita confirmar acciones
          sin plantar un modal delante. */}
      <ProveedorAvisos>
        {enReportarQR ? (
          <ReportarPorQR />
        ) : enTerminal ? (
          <TerminalApp />
        ) : enTienda ? (
          <TiendaApp />
        ) : enPanel ? (
          <ProveedorAuthPanel>
            <PanelApp />
          </ProveedorAuthPanel>
        ) : (
          <ProveedorAuth>
            {/* LA PANTALLA DE MANTENIMIENTO (carril consola, Entrega B, 20 sep
                2026; la monta el integrador): tapa la aplicación —y la puerta—
                solo cuando hay un corte programado en curso y el servidor no
                contesta. Sin nada programado no existe. */}
            <PantallaMantenimiento />
            <App />
          </ProveedorAuth>
        )}
      </ProveedorAvisos>
    </ErrorBoundary>
  </StrictMode>,
)

// Service worker: hace que la PWA cargue sin conexión y sea instalable.
// Solo en producción; en desarrollo interferiría con la recarga en caliente.
// EL REGISTRO DICE SI FALLÓ (DIRECTRIZ-PWA F1): hasta el 11 de octubre de
// 2026 iba con `.catch(() => {})`, y un worker que no se registraba dejaba
// la app sin arranque sin señal sin que nadie lo viera. Ahora el estado se
// anota y la pantalla «Instalar TEGO en tu teléfono» lo enseña.
if (import.meta.env.PROD && 'serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js')
      .then((reg) => anotarWorker({ estado: 'registrado', alcance: reg.scope }))
      .catch((e) => anotarWorker({ estado: 'fallo', motivo: String(e?.message || e) }));
    navigator.serviceWorker.ready.then(() => anotarWorker({ estado: 'listo' }));
  });
} else {
  anotarWorker({
    estado: 'no-disponible',
    motivo: import.meta.env.PROD ? 'El navegador no lo permite en esta dirección.' : 'desarrollo',
  });
}
